/**
 * Where a client's performance history actually begins, and what counts as a
 * meaningful denominator along the way.
 *
 * A broker series usually opens with points from before the account was funded:
 * a $0 row, or a partially-settled day worth a few dollars. Chaining a return
 * off those divides by ~nothing — an account funded $0 → $15,000 produced a
 * growth figure around 1,500,000% rather than the ~2.7% it actually earned.
 * Every surface needs the same answer to "when did this account start", so the
 * rule lives here rather than being re-derived per view.
 *
 * `materialityFloor` used to be private to clientReportingBenchmarkMetrics,
 * which is why the chart tabs (gating only on `prev > 0`) and the tiles
 * disagreed by orders of magnitude on the same account.
 */

/**
 * Smallest previous-day equity worth dividing by.
 *
 * Scaled to the account so it works for a $500 account and a $5m one: 0.5% of
 * the peak, 5% of the 90th-percentile day, floor of $10.
 */
export function materialityFloor(points) {
  const values = (points || []).map((point) => Number(point.value) || 0);
  const max = Math.max(...values, 0);
  const positive = values.filter((value) => value > 0).sort((a, b) => a - b);
  const p90 = positive.length
    ? positive[Math.min(positive.length - 1, Math.floor(positive.length * 0.9))]
    : 0;
  return Math.max(10, max * 0.005, p90 * 0.05);
}

/**
 * Index of the first funded point — the first value clearing the floor.
 *
 * Returns 0 when nothing clears it, so a tiny-but-real account still charts
 * rather than vanishing.
 */
export function firstFundedIndex(points) {
  if (!points?.length) return 0;
  const floor = materialityFloor(points);
  const found = points.findIndex((point) => (Number(point.value) || 0) >= floor);
  return found === -1 ? 0 : found;
}

/**
 * Drop leading pre-funding points.
 *
 * The opening deposit is capital, not profit, so performance is measured from
 * the moment the money is there. Fewer than two points left means there is
 * nothing to chain, and the caller should fall back to the untrimmed series.
 */
export function trimToFunded(points) {
  if (!points?.length) return points || [];
  const start = firstFundedIndex(points);
  if (start === 0) return points;
  const trimmed = points.slice(start);
  return trimmed.length >= 2 ? trimmed : points;
}

/** ISO date the account was first funded, or null. Drives the benchmark rebase. */
export function inceptionDate(points) {
  if (!points?.length) return null;
  return points[firstFundedIndex(points)]?.date || null;
}

/** Keep in step with server/utils/clientReportingPortfolioSeries.js */
const MAX_IMPLIED_DAILY_RETURN = 0.25;

function flowEquityMismatch(points, idx, flow) {
  if (idx < 0 || idx >= points.length) return Infinity;
  const value = Number(points[idx].value) || 0;
  if (idx === 0) {
    return Math.abs(value - flow);
  }
  const prev = Number(points[idx - 1].value) || 0;
  return Math.abs((value - prev) - flow);
}

/** Move documented CSD/CSW amounts to the day equity actually changed. */
export function relocateMisdatedFlows(points) {
  const out = (points || []).map((point) => ({
    ...point,
    netFlow: Number(point.netFlow) || 0,
    value: Number(point.value) || 0,
  }));
  if (out.length < 2) return out;

  const tolerance = Math.max(materialityFloor(out) * 0.05, 25);

  for (let i = 0; i < out.length; i += 1) {
    const amount = Number(out[i].netFlow) || 0;
    if (amount === 0) continue;

    const atCurrent = flowEquityMismatch(out, i, amount);
    if (atCurrent <= tolerance) continue;

    let bestIndex = i;
    let bestScore = atCurrent;
    for (let j = 1; j < out.length; j += 1) {
      const score = flowEquityMismatch(out, j, amount);
      if (score < bestScore) {
        bestScore = score;
        bestIndex = j;
      }
    }

    if (bestIndex !== i && (bestScore <= tolerance || bestScore < atCurrent * 0.5)) {
      out[i].netFlow = 0;
      out[bestIndex].netFlow += amount;
    }
  }

  return out;
}

/** Last-resort when Alpaca returned no CSD/CSW rows on sync. */
export function inferImpliedNetFlows(points) {
  const floor = materialityFloor(points);
  const out = (points || []).map((point) => ({
    ...point,
    netFlow: Number(point.netFlow) || 0,
    value: Number(point.value) || 0,
  }));
  if (out.length < 2) return out;

  let base = out[0].value;
  for (let i = 1; i < out.length; i += 1) {
    const point = out[i];
    if (point.netFlow !== 0) {
      base = point.value;
      continue;
    }

    const delta = point.value - base;
    if (delta === 0) {
      base = point.value;
      continue;
    }

    const prev = out[i - 1];
    const prevFlow = Number(prev?.netFlow) || 0;
    const depositPostedBeforeNav = prevFlow > 0
      && prev.value < prevFlow * 0.5
      && Math.abs((prev.value + prevFlow) - point.value) < Math.max(floor, 1);

    if (depositPostedBeforeNav) {
      base = point.value;
      continue;
    }

    const hadPriorFunding = out.slice(0, i).some((p) => (Number(p.value) || 0) >= floor);
    const ratio = base >= floor ? delta / base : null;
    const largeMove = Math.abs(delta) >= floor;
    const implausibleReturn = ratio !== null && Math.abs(ratio) >= MAX_IMPLIED_DAILY_RETURN;
    const firstFunding = !hadPriorFunding && base < floor && point.value >= floor && delta > 0;
    const doubling = base >= floor && delta > floor && ratio >= 1.0;
    const stillFunded = point.value >= floor;
    const impliedWithdrawal = stillFunded && delta < 0 && largeMove && implausibleReturn;
    const impliedDeposit = delta > 0 && largeMove && (
      firstFunding || doubling || implausibleReturn
    );

    if (impliedDeposit || impliedWithdrawal) {
      point.netFlow = delta;
    }

    base = point.value;
  }

  return out;
}

/** Relocate documented flows, infer per-day gaps, then trim — same as server. */
export function preparePortfolioSeries(rawPoints) {
  const normalized = (rawPoints || []).map((point) => ({
    date: point.date,
    value: Number(point.value ?? point.equity) || 0,
    netFlow: Number(point.netFlow ?? point.net_flow) || 0,
  }));
  if (!normalized.length) return [];
  const relocated = relocateMisdatedFlows(normalized);
  return trimToFunded(inferImpliedNetFlows(relocated));
}
